import { Navigate, Route, Routes } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { useI18n } from "@/contexts/I18nContext";
import { usePortalT } from "@/lib/portalI18n";
import PortalLanding from "@/pages/portal/PortalLanding";
import PortalSubmit from "@/pages/portal/PortalSubmit";
import PortalTrack from "@/pages/portal/PortalTrack";
import PortalReceipt from "@/pages/portal/PortalReceipt";
import PortalSuccess from "@/pages/portal/PortalSuccess";
import PortalVerifyInvoice from "@/pages/portal/PortalVerifyInvoice";
import FloatingFahad from "@/components/portal/FloatingFahad";
import FloatingContactDock from "@/components/portal/FloatingContactDock";

/** Top-level portal router — fully decoupled from the internal system. */
export default function ClientPortalPage() {
  const { dir, theme, lang } = useI18n();
  const t = usePortalT();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/portal/status`)
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ enabled: true, mode: "live" }));
  }, []);

  if (status === null) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: theme.textMuted }}>…</div>
    );
  }

  if (status.enabled === false) {
    return (
      <div dir={dir} style={{
        maxWidth: 480, margin: "120px auto", padding: 28,
        background: theme.surface, border: `1px solid ${theme.borderAccent}`,
        borderRadius: 16, textAlign: "center",
        fontFamily: "'Tajawal','Segoe UI',sans-serif", color: theme.text,
      }} data-testid="portal-disabled">
        <h1 style={{ color: theme.gold }}>{t("portal.disabled.title")}</h1>
        <p style={{ color: theme.textMuted }}>{t("portal.disabled.body")}</p>
      </div>
    );
  }

  return (
    <>
      {status.mode === "preview" && (
        <div style={banner("#facc15", "#1a1a1a")} data-testid="portal-banner-preview">
          {t("portal.banner.preview")}
        </div>
      )}
      {status.mode === "test" && (
        <div style={banner("#0ea5e9", "#fff")} data-testid="portal-banner-test">
          {t("portal.banner.test")}
        </div>
      )}
      <Routes>
        <Route index element={<PortalLanding />} />
        <Route path="submit" element={<PortalSubmit />} />
        <Route path="track" element={<PortalTrack />} />
        <Route path="track/:num" element={<PortalTrack />} />
        <Route path="track/:appNum/receipt" element={<PortalReceipt />} />
        <Route path="preview-receipt" element={<PortalReceipt />} />
        <Route path="success/:num" element={<PortalSuccess />} />
        <Route path="verify" element={<PortalVerifyInvoice />} />
        <Route path="*" element={<Navigate to="." replace />} />
      </Routes>
      {/* Fahad floats globally across every portal page */}
      <FloatingFahad theme={theme} dir={dir} lang={lang} />
      {/* Contact wheel sits just above Fahad, follows the customer as they scroll */}
      <FloatingContactDock />
    </>
  );
}

const banner = (bg, color) => ({
  width: "100%", padding: "10px 16px", background: bg, color,
  fontSize: 13, fontWeight: 700, textAlign: "center", letterSpacing: "0.3px",
});
